import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useAzureManager } from '@/auth/azure/azureManager'
import { tipologia_notifica } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface NotificationServiceStore {
  connection: HubConnection | null
  isConnected: boolean
  notifications: number[]
  connectionError: string | null
  lastActivity: number
  idleCheckInterval: NodeJS.Timeout | null
  hiddenTimeout: NodeJS.Timeout | null // Changed from hiddenTime
  visibilityHandler: (() => void) | null // Add this to track the handler
  activityHandler: (() => void) | null // Track focus activity handler

  connect: () => Promise<void>
  disconnect: () => Promise<void>
  notifyCurrentUser: (message: string, type?: tipologia_notifica, handled?: boolean) => Promise<void>
  receiveNotification: (notificationId: number) => Promise<void>
  handleNotification: (notificationId: number) => Promise<void>
  resetIdleTimer: () => void
  initializeIdleDetection: () => void // Renamed and separated
  cleanup: () => void // Add cleanup method
}
const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const IDLE_CHECK_INTERVAL = 60 * 1000 // Check every minute

export const useNotificationService = create<NotificationServiceStore>()(
  devtools((set, get) => ({
    connection: null,
    isConnected: false,
    notifications: [],
    connectionError: null,
    lastActivity: Date.now(),
    idleCheckInterval: null,
    hiddenTimeout: null,
    visibilityHandler: null,
    activityHandler: null,

    connect: async () => {
      try {
        console.log('[NotificationService] connect() called')
        // Create the connection with full URL
        const connection = new HubConnectionBuilder()
          .withUrl(`${import.meta.env.VITE_QSADMINAPI_HOST}/signalr/notifications`, {
            accessTokenFactory: async () => await useAzureManager.getState().getToken(),
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
          })
          .withAutomaticReconnect()
          .configureLogging(import.meta.env.PROD ? LogLevel.Error : LogLevel.Warning)
          .build()

        // Set up event handlers
        connection.on('NotificationChanged', async (notificationId: number) => {
          console.log('[NotificationService] NotificationChanged received:', notificationId)
          const changedNotification = await useQsAdminApiManager.getState().service.notifica(notificationId).query()
          if (get().notifications.includes(notificationId)) {
            if (changedNotification.data.gestita) {
              set((state) => ({
                notifications: state.notifications.filter((id) => id !== notificationId),
              }))
              console.log('[NotificationService] Removed handled notification from list:', notificationId)
            }
            useQsAdminApiManager.getState().tsQueryClient.invalidateQueries({
              queryKey: ['notification', notificationId],
            })
            console.log('[NotificationService] Invalidated query cache for notification:', notificationId)
          }
        })
        connection.on('NotificationReceived', (notificationId: number) => {
          console.log('[NotificationService] NotificationReceived for id:', notificationId)
          get().receiveNotification(notificationId)
        })
        connection.on('NotificationHandled', (notificationId: number) =>
          set((state) => ({
            notifications: state.notifications.filter((id) => id !== notificationId),
          })),
        )

        connection.onclose(() => {
          console.log('[NotificationService] SignalR connection closed')
          set({ isConnected: false })
        })

        connection.onreconnecting(() => {
          console.log('[NotificationService] SignalR attempting to reconnect...')
          set({ isConnected: false })
        })

        connection.onreconnected(() => {
          console.log('[NotificationService] SignalR reconnected')
          set({ isConnected: true })
        })

        // Start the connection
        // await connection.start()
        console.log('[NotificationService] SignalR connection established')

        // Update the store
        set({
          connection,
          isConnected: true,
          connectionError: null,
        })
      } catch (error) {
        console.error('[NotificationService] Error establishing SignalR connection:', error)
        set({
          connectionError: error instanceof Error ? error.message : 'Unknown connection error',
          isConnected: false,
        })
      }
    },

    disconnect: async () => {
      console.log('[NotificationService] disconnect() called')
      const { connection } = get()
      if (connection) {
        try {
          await connection.stop()
          set({
            connection: null,
            isConnected: false,
          })
          console.log('[NotificationService] SignalR connection closed')
        } catch (error) {
          console.error('[NotificationService] Error disconnecting from SignalR:', error)
        }
      }
    },

    // Initialize idle detection ONCE when the app starts, not on every connect
    initializeIdleDetection: () => {
      // Only initialize once
      if (get().visibilityHandler) {
        console.log('[NotificationService] Idle detection already initialized, skipping')
        return
      }

      console.log('[NotificationService] Initializing idle detection')

      // Idle checking
      const checkInterval = setInterval(() => {
        const { lastActivity, isConnected } = get()
        const elapsed = Date.now() - lastActivity
        console.log(
          '[NotificationService] Idle check tick. elapsed(ms)=',
          elapsed,
          'threshold(ms)=',
          IDLE_TIMEOUT,
          'isConnected=',
          isConnected,
        )
        if (isConnected && elapsed > IDLE_TIMEOUT) {
          console.log('[NotificationService] Disconnecting due to inactivity')
          get().disconnect()
        }
      }, IDLE_CHECK_INTERVAL)

      // Simple activity tracking - only track significant user actions
      const activityHandler = () => {
        console.log('[NotificationService] Activity detected: window focus')
        get().resetIdleTimer()
      }

      // Only listen to window focus/blur for activity
      window.addEventListener('focus', activityHandler)

      // Visibility handler
      const handleVisibilityChange = () => {
        const { hiddenTimeout, isConnected } = get()
        console.log(
          '[NotificationService] Visibility changed. document.hidden=',
          document.hidden,
          'isConnected=',
          isConnected,
        )

        if (document.hidden && isConnected) {
          // Start a timer to disconnect if hidden for too long
          const timeout = setTimeout(
            () => {
              if (document.hidden && get().isConnected) {
                console.log('[NotificationService] Disconnecting - tab hidden for too long')
                get().disconnect()
              }
            },
            5 * 60 * 1000,
          )

          console.log('[NotificationService] Started hidden disconnect timer (5m)')
          set({ hiddenTimeout: timeout })
        } else if (!document.hidden) {
          // Clear the disconnect timer if tab becomes visible
          if (hiddenTimeout) {
            clearTimeout(hiddenTimeout)
            console.log('[NotificationService] Cleared hidden disconnect timer')
            set({ hiddenTimeout: null })
          }

          // Reset activity timer when tab becomes visible
          get().resetIdleTimer()

          // Reconnect if disconnected and tab is now visible
          if (!isConnected && get().connection === null) {
            console.log('[NotificationService] Reconnecting after becoming visible')
            get().connect()
          }
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      set({
        idleCheckInterval: checkInterval,
        visibilityHandler: handleVisibilityChange,
        activityHandler,
      })
    },

    cleanup: () => {
      console.log('[NotificationService] cleanup() called')
      const { idleCheckInterval, hiddenTimeout, visibilityHandler, activityHandler } = get()

      if (idleCheckInterval) {
        clearInterval(idleCheckInterval)
        console.log('[NotificationService] Cleared idleCheckInterval')
      }

      if (hiddenTimeout) {
        clearTimeout(hiddenTimeout)
        console.log('[NotificationService] Cleared hiddenTimeout')
      }

      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler)
        console.log('[NotificationService] Removed visibilitychange handler')
      }

      if (activityHandler) {
        window.removeEventListener('focus', activityHandler)
        console.log('[NotificationService] Removed focus activity handler')
      }

      set({
        idleCheckInterval: null,
        hiddenTimeout: null,
        visibilityHandler: null,
        activityHandler: null,
      })
    },

    resetIdleTimer: () => {
      set({ lastActivity: Date.now() })
      console.log('[NotificationService] Idle timer reset')
    },

    notifyCurrentUser: async (message: string, type?: tipologia_notifica, handled?: boolean) => {
      console.log('[NotificationService] notifyCurrentUser called. type=', type, 'handled=', handled)
      get()
        .connection?.send('NotifyCurrentUser', message, type ?? 0, handled ?? false)
        .then(() => {
          console.log('[NotificationService] Notification sent')
        })
    },

    receiveNotification: async (notificationId: number) => {
      console.log('[NotificationService] receiveNotification called for id:', notificationId)
      set(
        (state) => ({
          notifications: [...state.notifications, notificationId],
        }),
        undefined,
        'receiveNotification',
      )
      console.log('[NotificationService] Current notifications:', get().notifications)
    },

    handleNotification: async (notificationId: number) => {
      console.log('[NotificationService] handleNotification called for id:', notificationId)
      await useQsAdminApiManager.getState().service.notifica(notificationId).patch({ gestita: true })
      useQsAdminApiManager.getState().tsQueryClient.invalidateQueries({
        queryKey: ['notification', notificationId],
      })
      console.log('[NotificationService] Marked notification as handled and invalidated cache for id:', notificationId)
    },
  })),
)
